




import React, { useEffect, useState } from "react";
import { Tabs, Tab, Box } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import AffiliateNotifications from "./AffiliateNotifications";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import '../../App.css';
import InfluencerHeader from "./InfluencerHeader";
import Navbar from "./Navbar";

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const NotificationInfluencer = () => {
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const notificationsPerPage = 10;
  const influencerId = localStorage.getItem("influencerID");

  useEffect(() => {
    if (!influencerId) {
      setError("Influencer ID not found in localStorage.");
      setLoading(false);
      return;
    }

    const fetchNotifications = async () => {
      try {
        const response = await fetch(
          `https://server-side-influencer.onrender.com/influencer/${influencerId}/notifications`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch notifications");
        }
        const data = await response.json();
        if (data.success) {
          setNotifications(data.notifications);
        } else {
          setError("No notifications found");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [influencerId]);

  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(
        "https://server-side-influencer.onrender.com/influencer/notifications",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            notificationIds: [notificationId],
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setNotifications((prev) =>
          prev.filter((n) => n.id !== notificationId)
        );
        toast.success("Notification deleted successfully!");
      } else {
        toast.error("Failed to delete notification.");
      }
    } catch (err) {
      toast.error("Error deleting notification: " + err.message);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 760);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const clearAllNotifications = async () => {
    const allIds = notifications.map((n) => n.id);
    try {
      const response = await fetch(
        "https://server-side-influencer.onrender.com/influencer/notifications",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            notificationIds: allIds,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        const updated = notifications.filter((n) => !allIds.includes(n.id));
        setNotifications(updated);

        if (updated.length === 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else if (updated.length === 0 && currentPage === 1) {
          setCurrentPage(1);
        }

        toast.success("All notifications cleared!");
      } else {
        toast.error("Failed to clear all notifications.");
      }
    } catch (err) {
      toast.error("Error clearing notifications: " + err.message);
    }
  };

  const indexOfLast = currentPage * notificationsPerPage;
  const indexOfFirst = indexOfLast - notificationsPerPage;
  const currentNotifications = notifications.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(notifications.length / notificationsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="flex flex-col h-screen">
      <div className="fixed top-0 left-0 right-0 z-40 bg-white shadow-md">
        <InfluencerHeader page="Notifications" />
      </div>

      <div className="flex flex-1 page-scroll pt-20">
        <div className="text-white fixed top-0 left-0 bottom-0 z-50">
          <Navbar />
        </div>

        <div
          className="flex-1 mt-0"
          style={{ marginLeft: isSmallScreen ? "0px" : "43px" }}
        >
          {/* MUI Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: "1rem", marginLeft: "45px"}}>
            <Tabs value={tabValue} onChange={(e, newVal) => setTabValue(newVal)}>
              <Tab label="Campaigns" />
              <Tab label="Other" />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            <h1 className="text-2xl font-bold mb-6 mt-4 ml-6">Notifications</h1>
            {notifications.length > 0 && !loading && (
              <div className="text-right mb-4 mr-4">
                <button
                  onClick={clearAllNotifications}
                  className="text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center transition"
                >
                  Clear All
                </button>
              </div>
            )}

            <div>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-5 shadow-lg flex justify-between items-center gap-4 mb-4 ml-5"
                  >
                    <div className="flex-1">
                      <Skeleton height={20} width={`80%`} />
                    </div>
                    <Skeleton circle width={30} height={30} />
                  </div>
                ))
              ) : error ? (
                <p className="text-xl text-red-500 mb-2">{error}</p>
              ) : currentNotifications.length === 0 ? (
                <p className="text-xl mb-2">No new notifications</p>
              ) : (
                currentNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="bg-white rounded-lg p-5 shadow-lg flex justify-between items-center gap-4 mb-4 ml-5"
                  >
                    <span className="font-semibold text-base text-gray-800">
                      {notification.message}
                    </span>
                    <IconButton
                      onClick={() => deleteNotification(notification.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                ))
              )}
            </div>

            {totalPages > 1 && !loading && (
              <div className="mt-4 flex justify-center space-x-4">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm text-white"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-xl">{`Page ${currentPage}`}</span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage * notificationsPerPage >= notifications.length}
                  className="px-4 py-2 bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm text-white"
                >
                  Next
                </button>
              </div>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <AffiliateNotifications />
          </TabPanel>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
};

export default NotificationInfluencer;
